<template>
  <div class="news-details">
    <news-hero
      :title="page.title"
      :abstract="page.abstract"
      :description="page.description"
      :date="page.date || page.dateCreated"
      :image="exists(page.image) ? page.image : null"
      :category="category"
      :back-to="backTo" />

    <blocks-loader
      v-if="exists(page.blocks)"
      :blocks="page.blocks" />

    <div
      v-if="page.htmlContent"
      class="l-container l-wrapper">
      <div
        class="news-details__content"
        v-html="page.htmlContent" />
    </div>

    <block-news-carousel
      v-if="exists(page.news)"
      :title="$t('general.mightInterestYou')"
      :news="page.news" />
  </div>
</template>

<script setup>
  import transitions from '~/animations/pages'
  import { useBackNavigation } from '~/composables/useBackNavigation'
  import { useArticleSchema } from '~/composables/useStructuredData'
  import { NEWS_DETAIL } from '~/graphql/pages/listing'

  const { locale, localeCodes } = useI18n()
  const { params } = useRoute()
  const env = useRuntimeConfig().public

  const {
    previewToken, previewParam, previewTimestamp
  } = usePreview()

  // Smart back navigation - returns to previous page or news index
  const { backTo } = useBackNavigation({ name: 'news' })

  const { data: page, error } = await useAsyncData(
    `newsdetail-${params.slug}-${locale.value}`,
    () =>
      useApi(
        NEWS_DETAIL,
        { uri: `news/${params.category}/${params.slug}` },
        {
          previewToken: previewToken.value,
          previewParam: previewParam.value
        }
      ),
    {
      transform: r => r.entry,
      watch: [ previewTimestamp ]
    }
  )

  if (!page.value || error.value) {
    console.error('Error fetching article data:', error.value)

    throw createError({
      statusCode: 404,
      statusMessage: 'Page Not Found'
    })
  }

  const category = computed(() => getFirst(page.value.categories))

  const setI18nParams = useSetI18nParams()

  setI18nParams(localeCodes.value.reduce((acc, _locale) => {
    if (_locale == locale.value) {
      acc[_locale] = {
        category: params.category,
        slug: params.slug
      }
    } else {
      acc[_locale] = {
        category: category.value?.localized.find(c => c.site == _locale)?.slug ?? null,
        slug: page.value?.localized?.find(lp => lp.site == _locale)?.slug ?? null
      }
    }
    return acc
  }, {}))

  await nextTick()
  const layout = useLayoutStore()

  layout.__canShowLocales = true

  // SEO Meta
  useSeoData(page)

  // ----------------------------- STRUCTURED DATA -----------------------------
  // Add Article schema
  const articleSchema = computed(() =>
    useArticleSchema(page.value, env.BASE_URL, locale.value, 'news')
  )

  watchEffect(() => {
    if (articleSchema.value) {
      useJsonld(articleSchema.value)
    }
  })

  definePageMeta({
    pageTransition: {
      name: 'page-transition',
      mode: 'out-in',
      ...transitions
    }
  })
</script>

<style lang="scss">
.news-details {
  width: 100%;

  &__content {
    @include blocks-y-spacing;

    display: flex;
    flex-direction: column;
    gap: rem(24px);
    grid-column: 1 / span 12;

    @include mq(medium) {
      grid-column: 2 / span 10;
    }

    a,
    button {
      text-decoration: underline;
    }

    p {
      margin: rem(16px) 0;
    }

    iframe {
      width: 100%;
      min-height: 768px;
    }

    img {
      margin: 0 auto;
      max-width: 100%;
      object-fit: contain;

      @include mq(medium) {
        max-width: 768px;
      }

      &.image-inline {
        display: inline !important;
        max-width: none !important;
        margin: 0 !important;
        object-fit: initial !important;
      }
    }
  }
}
</style>

<style lang="scss" scoped>
.news-details__content {
  @include ckeditor-styles;

  // I paragrafi di testo restano a larghezza piena cosi l'allineamento
  // impostato nel CMS (text-align left/center/right) viene rispettato.
  // Senza questo, il `margin: 0 auto` del mixin in un container flex
  // restringe e centra i paragrafi corti, ignorando l'allineamento.
  :deep(p) {
    margin: 0 0 rem(24px);
  }

  // I paragrafi che contengono solo un'immagine restano centrati
  // (comportamento storico delle immagini in-contenuto).
  :deep(.image),
  :deep(p:has(img)) {
    margin: 0 auto rem(24px);
    text-align: center;
  }
}
</style>
